<template>
  <div
    class="manager-event-messages"
    v-if="loaded && subscriptionTier === 'PRO'"
  >
    <h4 class="page-title">Create messages for your audience</h4>
    <p class="page-subtitle large" v-if="eventData.eventMessages.length > 0">
      Messages you’ve created, along with any user comments, are shown below.
    </p>
    <p class="page-subtitle large" v-else>
      Create a message and choose who should see it. <br />
    </p>

    <StandardSolidButton
      v-if="eventData.eventMessages.length > 0 || createNewMessage == false"
      class="create-message-button"
      title="Create message"
      :desktop-fluidity="true"
      :fluid="false"
      @click="expandCreateNewMessage()"
    />
    <slide-up-down
      class="new-message-wrapper"
      id="new-message"
      :active="createNewMessage"
      :duration="300"
    >
      <div class="new-message">
        <div class="title">
          <form autocomplete="off">
            <DefaultTextInput
              v-model="eventMessageSubject"
              @input="
                storeMessage(
                  eventMessageSubject,
                  eventMessageBody,
                  eventMessageAudience
                )
              "
              :maxlength="255"
              placeholder="Add message title (required)"
              :fat="true"
            />
          </form>
        </div>
        <div class="new-message-body">
          <vue-editor
            :placeholder="'Add message body (optional)'"
            :class="{ error: eventMessageBody.length > 2500 }"
            v-model="eventMessageBody"
            @input="
              storeMessage(
                eventMessageSubject,
                eventMessageBody,
                eventMessageAudience
              )
            "
            :editor-toolbar="customToolbar"
          />
        </div>
        <div class="new-message-audience">
          <h5>Visible for</h5>
          <DefaultRadioButton
            groupname="audience"
            :value="eventMessageAudience"
            :options="{
              title: 'Public (everyone)',
              value: 'PUBLIC',
            }"
            @change="
              eventMessageAudience = $event
              storeMessage(
                eventMessageSubject,
                eventMessageBody,
                eventMessageAudience
              )
            "
          />
          <DefaultRadioButton
            groupname="audience"
            :value="eventMessageAudience"
            :options="{
              title:
                'Confirmed ' +
                teamVsPlayerWording(eventData.singles, true, false),
              value: 'ACCEPTED',
            }"
            @change="
              eventMessageAudience = $event
              storeMessage(
                eventMessageSubject,
                eventMessageBody,
                eventMessageAudience
              )
            "
          />
          <DefaultRadioButton
            groupname="audience"
            :value="eventMessageAudience"
            :options="{
              title:
                'Waiting ' +
                teamVsPlayerWording(eventData.singles, true, false),
              value: 'PENDING',
            }"
            @change="
              eventMessageAudience = $event
              storeMessage(
                eventMessageSubject,
                eventMessageBody,
                eventMessageAudience
              )
            "
          />
        </div>
        <div class="new-message-actions">
          <StandardBorderedButton
            title="Cancel"
            :fluid="false"
            @click="cancelNewMessage()"
          />
          <StandardSolidButton
            title="Post"
            :locked="postInProgress"
            :fluid="false"
            :disabled="!newMessageSaveable"
            @click="
              saveNewMessage(
                eventMessageSubject,
                eventMessageBody,
                eventMessageAudience
              )
            "
          />
        </div>
      </div>
      <div class="push-info-banner">
        <p>
          <b>
            {{ teamVsPlayerWording(eventData.singles, true, true) }} using Tjing
            for iOS and Android will receive push notifications</b
          >
        </p>
      </div>
    </slide-up-down>
    <div
      class="message"
      v-for="message in eventData.eventMessages"
      :key="message.id"
    >
      <slide-up-down
        class="message-wrapper"
        :active="
          eventMessageToEdit != null && eventMessageToEdit.id == message.id
        "
        :duration="300"
      >
        <div class="update-message">
          <div class="title">
            <DefaultTextInput
              v-model="message.subject"
              :maxlength="255"
              :invalid="message.subject.length == 0"
              placeholder="Add message title (required)"
              :fat="true"
            />
          </div>
          <div class="update-message-body">
            <vue-editor
              placeholder="Add message body (optional)"
              :class="{ error: message.message.length > 2500 }"
              v-model="message.message"
              :editor-toolbar="customToolbar"
            />
          </div>
          <div class="update-message-audience">
            <h5>Visible for</h5>
            <DefaultRadioButton
              :groupname="message.id + 'update-audience'"
              :value="message.audience"
              :options="{
                title: 'Public (everyone)',
                value: 'PUBLIC',
              }"
              @change="message.audience = $event"
            />
            <DefaultRadioButton
              :groupname="message.id + 'update-audience'"
              :value="message.audience"
              :options="{
                title:
                  'Confirmed ' +
                  teamVsPlayerWording(eventData.singles, true, false),
                value: 'ACCEPTED',
              }"
              @change="message.audience = $event"
            />
            <DefaultRadioButton
              :groupname="message.id + 'update-audience'"
              :value="message.audience"
              :options="{
                title:
                  'Waiting ' +
                  teamVsPlayerWording(eventData.singles, true, false),
                value: 'PENDING',
              }"
              @change="message.audience = $event"
            />
          </div>
          <div class="update-message-actions">
            <StandardBorderedButton
              title="Cancel"
              :fluid="false"
              @click="eventMessageToEdit = null"
            />
            <StandardSolidButton
              title="Update"
              :fluid="false"
              :disabled="!existingMessageSaveable(message)"
              @click="updateMessage(message)"
            />
          </div>
        </div>
      </slide-up-down>
      <slide-up-down
        class="message-wrapper"
        :active="
          eventMessageToEdit == null ||
          (eventMessageToEdit != null && eventMessageToEdit.id != message.id)
        "
        :duration="300"
      >
        <div class="message-subject">
          <p>
            <b>{{ message.subject }}</b>
          </p>
        </div>
        <div class="message-body" v-if="message.message">
          <vue-markdown :source="message.message" />
        </div>
        <div class="message-footer">
          <div class="audience-label">
            <div class="public" v-if="message.audience == 'PUBLIC'">
              <Watch color="grass" />
              <p>Everyone</p>
            </div>
            <div class="accepted" v-else-if="message.audience == 'ACCEPTED'">
              <Watch color="ocean" />
              <p>
                Confirmed
                {{ teamVsPlayerWording(eventData.singles, true, false) }}
              </p>
            </div>
            <div class="waiting" v-else-if="message.audience == 'PENDING'">
              <Watch color="sunset" />
              <p>
                Waiting
                {{ teamVsPlayerWording(eventData.singles, true, false) }}
              </p>
            </div>
          </div>
          <div class="author-info">
            <p>
              Posted
              <b>{{ dateFormatter(message.createdAt, 'D MMM, HH:mm') }}</b>
              by {{ message.author.firstName }} {{ message.author.lastName }}
            </p>
            <p
              v-if="message.createdAt != message.updatedAt"
              class="round-separator desktop-only"
            ></p>

            <p v-if="message.createdAt != message.updatedAt" class="updater">
              Last updated
              <b>{{ dateFormatter(message.updatedAt, 'D MMM, HH:mm') }}</b>
              by {{ message.updatedBy.firstName }}
              {{ message.updatedBy.lastName }}
            </p>
            <p class="round-separator desktop-only"></p>
            <div class="comments-counter">
              <Comments />
              <p class="message-comments-count">
                {{ message.repliesCount }}
                {{ singularPlural('comment', message.repliesCount == 1) }}
              </p>
            </div>
          </div>
          <div class="actions">
            <StandardSmallBorderedButton
              :fluid="true"
              @click="deleteMessagePopUp(message)"
              title="Delete"
            />
            <StandardSmallSolidButton
              :fluid="true"
              @click="eventMessageToEdit = message"
              title="Edit"
            />
          </div>
        </div>
        <div class="message-comments">
          <div class="comment" v-for="comment in message.replies">
            <UserProfileAvatar
              :profileImageUrl="comment.createdBy.profile.profileImageUrl"
              :backgroundColor="
                adminUserIds.includes(comment.createdBy.id)
                  ? 'ocean'
                  : 'twilight'
              "
              :initials="
                comment.createdBy.firstName.charAt(0) +
                comment.createdBy.lastName.charAt(0)
              "
            />
            <div class="comment-body">
              <p class="author">
                <b
                  >{{ comment.createdBy.firstName }}
                  {{ comment.createdBy.lastName }}</b
                >
                <span
                  class="admin-suffix"
                  v-if="adminUserIds.includes(comment.createdBy.id)"
                >
                  (admin)</span
                >
              </p>
              <p class="removed-message" v-if="comment.isDeleted">
                This comment has been removed.
              </p>
              <p
                v-if="comment.isVisible"
                class="comment-message"
                :class="{ 'deleted-message': comment.isDeleted }"
              >
                {{ comment.message }}
              </p>
            </div>
            <div class="comment-footer">
              <div class="time-wrapper">
                <TjingToolTip
                  :text="dateFormatter(comment.createdAt, 'D MMM YYYY HH:mm')"
                  :hideOnDesktop="false"
                  :bottomOffset="24"
                >
                  <p class="time posted-time">
                    {{ timeSince(comment.createdAt) }}
                  </p>
                </TjingToolTip>
                <p class="round-separator"></p>

                <template v-if="comment.isDeleted">
                  <p
                    class="reply"
                    v-if="comment.isVisible"
                    @click="comment.isVisible = false"
                  >
                    Hide
                  </p>
                  <p class="reply" v-else @click="comment.isVisible = true">
                    View
                  </p>
                  <p class="round-separator"></p>
                  <p class="delete" @click="toggleDeleted(comment)">Restore</p>
                </template>
                <template v-else>
                  <p class="reply" @click="commentToReplyTo = comment">Reply</p>
                  <p class="round-separator"></p>
                  <p
                    class="delete"
                    @click="toggleDeleted(comment)"
                    v-if="!comment.isDeleted"
                  >
                    Delete
                  </p>
                </template>
              </div>
              <div
                class="show-more"
                v-if="
                  comment.repliesCount > comment.replies.length &&
                  comment.showReplies === false
                "
                @click="loadReplies(comment)"
              >
                <p>
                  <b>Show replies</b> ({{
                    comment.repliesCount - comment.replies.length
                  }})
                </p>
              </div>
              <div
                class="show-more"
                v-else-if="
                  comment.repliesCount > 0 && comment.showReplies === false
                "
                @click="comment.showReplies = true"
              >
                <p><b>Show replies</b> ({{ comment.repliesCount }})</p>
              </div>
              <div
                class="show-more"
                v-if="
                  comment.replies.length > 0 && comment.showReplies === true
                "
                @click="comment.showReplies = false"
              >
                <p><b>Hide replies</b></p>
              </div>
            </div>
            <div class="sub-replies" v-if="comment.showReplies">
              <div class="sub-reply" v-for="reply in comment.replies">
                <UserProfileAvatar
                  :profileImageUrl="reply.createdBy.profile.profileImageUrl"
                  :backgroundColor="
                    adminUserIds.includes(reply.createdBy.id)
                      ? 'ocean'
                      : 'twilight'
                  "
                  :initials="
                    reply.createdBy.firstName.charAt(0) +
                    reply.createdBy.lastName.charAt(0)
                  "
                />
                <div class="sub-reply-body">
                  <p class="author">
                    <b
                      >{{ reply.createdBy.firstName }}
                      {{ reply.createdBy.lastName }}</b
                    >
                    <span
                      class="admin-suffix"
                      v-if="adminUserIds.includes(comment.createdBy.id)"
                    >
                      (admin)</span
                    >
                  </p>
                  <p class="removed-message" v-if="reply.isDeleted">
                    This reply has been removed.
                  </p>
                  <p
                    class="sub-reply-message"
                    v-if="reply.isVisible"
                    :class="{ 'deleted-message': reply.isDeleted }"
                  >
                    {{ reply.message }}
                  </p>
                </div>
                <div class="sub-reply-footer">
                  <div class="time-wrapper">
                    <p class="posted-time">
                      {{ timeSince(reply.createdAt) }}
                    </p>
                    <p class="round-separator" v-if="!comment.isDeleted"></p>
                  </div>
                  <template v-if="reply.isDeleted && !comment.isDeleted">
                    <p
                      class="reply"
                      v-if="reply.isVisible"
                      @click="reply.isVisible = false"
                    >
                      Hide
                    </p>
                    <p class="reply" v-else @click="reply.isVisible = true">
                      View
                    </p>
                    <p class="round-separator"></p>
                    <p class="delete" @click="toggleDeleted(reply)">Restore</p>
                  </template>
                  <template v-else-if="!comment.isDeleted">
                    <p
                      class="delete"
                      @click="toggleDeleted(reply)"
                      v-if="!reply.isDeleted"
                    >
                      Delete
                    </p>
                  </template>
                </div>
              </div>
            </div>
            <div
              class="show-more base-padding"
              v-if="
                comment.repliesCount > comment.replies.length &&
                comment.showReplies === true
              "
              @click="loadReplies(comment)"
            >
              <p>
                <b>Show more replies</b> ({{
                  comment.repliesCount - comment.replies.length
                }})
              </p>
            </div>
          </div>
        </div>
        <div
          class="load-more-comments"
          v-if="message.replies.length < message.repliesCount"
          @click="loadComments(message)"
        >
          <p class="small"><b>Show more comments</b></p>
        </div>
        <div class="comment-section">
          <CommentField
            placeholder="Write a comment"
            :eventMessage="message"
            :messageReply="commentToReplyTo"
            :messageReplyId="commentToReplyTo ? commentToReplyTo.id : null"
            :avatarBackgroundColor="
              adminUserIds.includes(loggedInUserId) ? 'ocean' : 'twilight'
            "
            @cancelReply="commentToReplyTo = null"
            @sendMessage="createComment(message, $event)"
            @sendNestedMessage="
              createNestedReply(message.id, commentToReplyTo, $event)
            "
          />
        </div>
      </slide-up-down>
    </div>
    <sweet-modal
      :enable-mobile-fullscreen="false"
      blocking
      ref="deletemessage"
      hide-close-button
    >
      <h3>Delete message</h3>
      <p>Are you sure you want to delete this message?</p>

      <div class="buttons">
        <StandardSolidButton
          title="Cancel"
          :fluid="false"
          @click="cancelDeleteMessage()"
        />
        <StandardBorderedButton
          class="forwards"
          title="Delete"
          :fluid="false"
          @click="deleteMessage(messageToDelete.id)"
        />
      </div>
    </sweet-modal>
  </div>
  <section class="no-subscription" v-else-if="loaded">
    <div class="locked-icon">
      <LockedMessages />
    </div>

    <p class="no-subscription-copy">
      <b>Get your message out there!</b>
    </p>
    <p class="sub-copy">Type a message and choose your audience.</p>
    <StandardSolidButton
      class="get-started-button"
      title="Upgrade now"
      :desktopFluidity="true"
      @click="$router.push({ name: 'pricing' })"
    />
  </section>
</template>

<script>
import moment from 'moment'

import SlideUpDown from 'vue-slide-up-down'
import ClickOutside from 'vue-click-outside'
import DefaultTextInput from '@/components/UIElements/DefaultTextInput'
import { VueEditor } from 'vue2-editor'
import VueMarkdown from 'vue-markdown'
import StandardSolidButton from '@/components/UIElements/StandardSolidButton'
import StandardBorderedButton from '@/components/UIElements/StandardBorderedButton'
import StandardSmallSolidButton from '@/components/UIElements/StandardSmallSolidButton'
import StandardSmallBorderedButton from '@/components/UIElements/StandardSmallBorderedButton'
import DefaultRadioButton from '@/components/UIElements/DefaultRadioButton'
import UserProfileAvatar from '@/components/UIElements/UserProfileAvatar'
import CommentField from '@/components/CommentField'
import wordingUtils from '@/mixins/wordingUtils.js'
import dateFormats from '@/mixins/dateFormats.js'
import Comments from '@/assets/icons/Comments'
import LockedMessages from '@/assets/icons/LockedMessages'
import Padlock from '@/assets/icons/Padlock'
import Watch from '@/assets/icons/Watch'

export default {
  name: 'EventMessages',
  mixins: [wordingUtils, dateFormats],
  components: {
    DefaultTextInput,
    VueEditor,
    VueMarkdown,
    StandardSolidButton,
    StandardBorderedButton,
    SlideUpDown,
    StandardSmallSolidButton,
    StandardSmallBorderedButton,
    DefaultRadioButton,
    UserProfileAvatar,
    CommentField,
    Comments,
    Watch,
    LockedMessages,
  },
  directives: {
    ClickOutside,
  },
  data() {
    return {
      eventData: {},
      loaded: false,
      postInProgress: false,
      adminUserIds: [],
      commentToReplyTo: null,
      createNewMessage: false,
      eventMessageToEdit: null,
      eventMessageToDelete: null,
      eventMessageBody: '',
      eventMessageBodyError: false,
      eventMessageSubject: '',
      eventMessageAudience: 'PUBLIC',
      customToolbar: [
        ['bold', 'italic', 'underline', 'link'],
        [{ list: 'ordered' }, { list: 'bullet' }],
      ],
    }
  },
  watch: {
    $route: 'loadData',
    loaded: function (newVal) {
      this.$nextTick(() => {
        this.disableTabbingToButtons()
      })
    },
  },
  computed: {
    subscriptionTier() {
      return this.$store.getters.getManagerMenuData?.subscriptionTier
    },
    loggedInUserId() {
      return this.$store.getters.userInfo.sessionInfo.userId
    },
    deviceWidth() {
      return this.$store.getters.deviceWidth
    },
    newMessageSaveable() {
      let isSaveable = true
      if (
        this.eventMessageSubject.length < 3 ||
        this.eventMessageSubject.length > 255
      ) {
        isSaveable = false
      }
      if (this.eventMessageBody.length > 2500) isSaveable = false

      return isSaveable
    },
  },
  methods: {
    storeMessage(title, body, audience) {
      const message = {
        title,
        body,
        audience,
        eventId: this.$route.params.eventId,
        date: new Date(),
      }

      const unfinishedMessages = localStorage.getItem(`unfinishedEventMessages`)

      if (unfinishedMessages) {
        const unfinishedMessagesArray = JSON.parse(unfinishedMessages)
        const withoutExisting = unfinishedMessagesArray.filter(
          (x) => x.eventId !== this.$route.params.eventId
        )
        localStorage.setItem(
          `unfinishedEventMessages`,
          JSON.stringify([...withoutExisting, message])
        )
      } else {
        localStorage.setItem(
          `unfinishedEventMessages`,
          JSON.stringify([message])
        )
      }
    },
    disableTabbingToButtons() {
      const qlButtons = document.getElementsByClassName('ql-formats')

      for (var i = 0; i < qlButtons.length; i++) {
        for (let index = 0; index < qlButtons[i].children.length; index++) {
          qlButtons[i].children[index].setAttribute('tabindex', -1)
        }
      }
    },
    async toggleDeleted(reply) {
      try {
        const deletedMessage = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
              mutation{
                UpdateEventMessageReply(eventMessageReplyId:"${reply.id}",
                isDeleted: ${!reply.isDeleted}){
                  id
                }
              }
            `,
          },
        })

        reply.isDeleted = !reply.isDeleted
        reply.isDeleted ? (reply.isVisible = false) : (reply.isVisible = true)

        if (deletedMessage.data.errors) {
          throw deletedMessage.data.errors[0]
        }

        this.$store.dispatch('showMessage', {
          show: true,
          message: reply.isDeleted ? 'Message removed' : 'Message restored',
          type: 'success',
        })
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: err.message || 'Error',
          type: 'error',
        })
        console.log(err)
      }
    },
    expandCreateNewMessage() {
      this.createNewMessage = true
      if (this.deviceWidth < 1200) {
        this.$nextTick(() =>
          this.$scrollTo('#new-message', {
            offset: -20,
          })
        )
      }
    },
    existingMessageSaveable(message) {
      let isSaveable = true
      if (message.subject.length == 0 || message.subject.length > 255) {
        isSaveable = false
      }
      if (message.message.length > 2500) isSaveable = false

      return isSaveable
    },
    deleteMessagePopUp(message) {
      this.messageToDelete = message
      this.$refs.deletemessage.open()
    },
    cancelDeleteMessage() {
      this.$refs.deletemessage.close()
      this.messageToDelete = null
    },
    async cancelEditMessage() {
      await this.loadData()
      this.eventMessageToEdit = null
    },
    async deleteMessage(messageId) {
      try {
        const newMessage = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
              mutation{
                DeleteEventMessage(messageId:"${messageId}")
              }
            `,
          },
        })
        this.$refs.deletemessage.close()
        this.loadData()
      } catch (err) {
        console.log(err)
      }
    },
    cancelNewMessage() {
      this.createNewMessage = false
      this.eventMessageAudience = 'PUBLIC'
      this.eventMessageSubject = ''
      this.eventMessageBody = ''
    },
    async updateMessage(message) {
      try {
        const newMessage = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
            mutation UpdateEventMessage($UpdateEventMessage: UpdateEventMessageInput!){
              UpdateEventMessage(messageId:"${message.id}",input:$UpdateEventMessage){
                id
              }
            }
          `,
            variables: {
              UpdateEventMessage: {
                subject: message.subject,
                message: message.message,
                audience: message.audience,
              },
            },
          },
        })
        this.cancelEditMessage()
      } catch (err) {
        console.log(err)
      }
    },
    async saveNewMessage(subject, message, audience) {
      this.postInProgress = true
      try {
        const newMessage = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
          mutation CreateEventMessage($CreateEventMessage: CreateEventMessageInput!){
            CreateEventMessage(eventId:"${this.$route.params.eventId}",input:$CreateEventMessage){
              id
            }
          }
          `,
            variables: {
              CreateEventMessage: {
                messageType: 'EVENT_POST',
                subject,
                message,
                audience,
              },
            },
          },
        })
        this.postInProgress = false
        this.loadData()
        this.createNewMessage = false
        this.eventMessageSubject = ''
        this.eventMessageAudience = 'PUBLIC'
        this.eventMessageBody = ''

        const unfinishedEventMessages = localStorage.getItem(
          `unfinishedEventMessages`
        )
        if (unfinishedEventMessages) {
          const unfinishedEventMessagesArray = JSON.parse(
            unfinishedEventMessages
          )
          const withoutExisting = unfinishedEventMessagesArray.filter(
            (x) => x.eventId !== this.$route.params.eventId
          )
          localStorage.setItem(
            `unfinishedEventMessages`,
            JSON.stringify(withoutExisting)
          )
        }
      } catch (err) {
        console.log(err)
      }
    },
    async loadData() {
      try {
        const eventMessages = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
              {
                event(eventId:"${this.$router.currentRoute.params.eventId}"){
                  singles
                  tour{
                    administrators{
                      userId
                    }
                  }
                  eventMessages{
                    id
                    eventId
                    subject
                    message
                    audience
                    createdAt
                    updatedAt
                    userReadReceipt {
                      id
                      userId
                      messageId
                      readAt
                    }
                    author{
                      id
                      firstName
                      lastName
                    }
                    updatedBy{
                      id
                      firstName
                      lastName
                    }
                    repliesCount
                    replies{
                      id
                      eventMessageId
                      message
                      isDeleted
                      createdAt
                      createdBy{
                        id
                        firstName
                        lastName
                        profile{
                          profileImageUrl
                        }
                      }
                      updatedAt
                      updatedBy{
                        id
                      }
                      repliesCount
                    }
                  }
                }
              }
            `,
          },
        })

        eventMessages.data.data.event.eventMessages.forEach((x) => {
          x.replies.forEach((y) => {
            y.replies = []
            y.showReplies = false
            y.isVisible = y.isDeleted ? false : true
          })
        })
        this.eventData = eventMessages.data.data.event
        this.eventData.eventMessages.length == 0
          ? (this.createNewMessage = true)
          : (this.createNewMessage = false)

        this.adminUserIds = [
          ...eventMessages.data.data.event.tour.administrators.map(
            (x) => x.userId
          ),
        ]

        const unfinishedEventMessages = localStorage.getItem(
          `unfinishedEventMessages`
        )
        if (unfinishedEventMessages) {
          const unfinishedEventMessagesArray = JSON.parse(
            unfinishedEventMessages
          )
          const unfinishedMessage = unfinishedEventMessagesArray.find(
            (x) => x.eventId === this.$route.params.eventId
          )
          if (unfinishedMessage) {
            this.eventMessageSubject = unfinishedMessage.title
            this.eventMessageBody = unfinishedMessage.body
            this.eventMessageAudience = unfinishedMessage.audience
          }

          const withoutOld = unfinishedEventMessagesArray.filter((x) =>
            moment(x.date).isBefore(moment().subtract(7, 'days'))
          )

          localStorage.setItem(
            `unfinishedEventMessages`,
            JSON.stringify(withoutOld)
          )
        }

        this.loaded = true
      } catch (e) {
        console.log(e)
      }
    },
    async loadComments(message) {
      const skip = message.replies ? message.replies.length : 0
      const first = 10
      try {
        const comments = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
              {
                eventMessageComments(eventMessageId:"${message.id}", first: ${first}, skip: ${skip}) {
                  id
                  eventMessageId
                  message
                  isDeleted
                  createdAt
                  createdBy {
                    id
                    firstName
                    lastName
                    profile{
                      profileImageUrl
                    }
                  }
                  updatedAt
                  updatedBy {
                    id
                  }
                  repliesCount
                }
              }
            `,
          },
        })

        comments.data.data.eventMessageComments.forEach((x) => {
          x.showReplies = false
          x.isVisible = x.isDeleted ? false : true
          x.replies = []
        })

        message.replies = [
          ...message.replies,
          ...comments.data.data.eventMessageComments,
        ]
      } catch (err) {
        console.log(err)
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Something went wrong',
          type: 'error',
        })
      }
    },
    async loadReplies(comment) {
      const skip = comment.replies ? comment.replies.length : 0
      const first = 10
      try {
        const commentReplies = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
            {
              eventMessageCommentReplies(replyId:"${comment.id}",first:${first},skip:${skip}){
                id
                message
                isDeleted
                createdAt
                updatedAt
                createdBy{
                  id
                  firstName
                  lastName
                  profile{
                    profileImageUrl
                  
                  }
                }
              }
            }
            `,
          },
        })

        commentReplies.data.data.eventMessageCommentReplies.forEach((x) => {
          x.isVisible = x.isDeleted ? false : true
        })

        comment.showReplies = true
        comment.replies = [
          ...comment.replies,
          ...commentReplies.data.data.eventMessageCommentReplies,
        ]
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Something went wrong',
          type: 'error',
        })
      }
    },
    async createComment(eventMessage, message) {
      try {
        const commentReplies = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
              mutation CreateEventMessageReply($message: String!){
                CreateEventMessageReply(
                  eventMessageId:"${eventMessage.id}",
                  message: $message){
                  id
                }
              }
            `,
            variables: {
              message: message,
            },
          },
        })

        this.loadData()
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Something went wrong',
          type: 'error',
        })
      }
    },
    async createNestedReply(eventMessageId, parentMessage, message) {
      try {
        const commentReplies = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
              mutation CreateEventMessageReply($message: String!){
                CreateEventMessageReply(
                  eventMessageId:"${eventMessageId}",
                  parentMessageId: "${parentMessage.id}",
                  message: $message){
                  id
                }
              }
            `,
            variables: {
              message: message,
            },
          },
        })

        parentMessage.repliesCount++
        this.loadReplies(parentMessage)
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Something went wrong',
          type: 'error',
        })
      }
    },
  },
  mounted() {
    this.loadData()
  },
  beforeCreate() {},
  destroyed() {},
}
</script>

<style lang="scss">
.manager-event-messages {
  .message {
    .message-body,
    .quillWrapper {
      @import '@/styles/Typography';
      ul {
        list-style-type: disc;
        padding-left: 1.5em;
        margin-bottom: 1rem;
      }
    }
  }
}

@media (min-width: 1024px) {
  .quillWrapper {
    margin-bottom: 46px;

    .ql-container {
      margin-top: 0;
    }
  }
}
</style>

<style scoped lang="scss">
@import '@/styles/Typography.scss';

.locked-icon {
  position: relative;
  display: inline-block;
  > svg {
    width: 39px;
    height: 30px;
  }
}
.no-subscription {
  margin-top: 32px;
  padding: 0 15px;
  text-align: center;
}

.no-subscription-copy {
  font-size: 1rem;
  line-height: 1.5rem;
  margin-top: 32px;
}
.sub-copy {
  margin-top: 1rem;
  font-size: 1rem;
}

.get-started-button {
  margin-top: 32px;
}

.desktop-only {
  display: none;
}
.admin-suffix {
  color: $strom;
}
.manager-event-messages {
  padding: 0 12px;
}

.page-title {
  padding: 0 6px;
}

.page-subtitle {
  margin-top: 12px;
  padding: 0 6px;
  line-height: 1.5rem;
}

.create-message-button {
  margin: 20px 0 32px;
}

.new-message-wrapper {
  margin-top: 18px;
  margin-bottom: 20px;
}

.new-message {
  background: white;
  padding: 16px 12px 32px 12px;
  border-radius: 10px 10px 0 0;
  border: 1px solid $sleet;
  border-bottom: none;
}

.new-message-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
  button {
    max-width: 47%;
  }
}

.new-message-audience {
  .default-radio-buttons {
    margin-top: 16px;
  }
}

.push-info-banner {
  background: #fff4d9;
  border: 1px solid $sleet;
  border-top: none;
  border-radius: 0 0 10px 10px;
  padding: 12px;
  p {
    color: $sunrise;
    text-align: center;
  }
}
.update-message {
  background: white;
  padding: 16px;
  border-radius: 10px;
  width: 100%;
}

.update-message-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  button {
    max-width: 47%;
    p {
      @include Gilroy-Bold;
    }
  }
}

.update-message-audience {
  .default-radio-buttons {
    margin-top: 16px;
  }
}
.message {
  border-radius: 10px;
  border: 1px solid $sleet;
  background: white;
  margin-bottom: 20px;
}

.message-subject {
  padding: 16px 16px 0 16px;
}
.message-body {
  padding: 12px 16px 0 16px;
  :deep(p) {
    overflow-wrap: anywhere;
  }
}

.audience-label {
  display: flex;
  justify-content: flex-start;
  margin-right: 18px;
  p {
    font-size: 0.625rem;
    text-transform: uppercase;
    @include Gilroy-Bold;
    letter-spacing: 0.6px;
  }

  > div {
    border-radius: 12px;
    display: flex;
    align-items: center;
    padding: 5px 10px 5px 10px;

    svg {
      width: 15px;
      height: 8px;
      margin-right: 6px;
    }

    &.public {
      background: #d9f8f0;
      border: 1px solid #a6eddb;
      p {
        color: $grass;
      }
    }
    &.accepted {
      background: #d9f4f5;
      border: 1px solid #a7e5e8;
      p {
        color: $ocean;
      }
    }
    &.waiting {
      background: #ffe9e3;
      border: 1px solid #ffccbe;
      p {
        color: $sunset;
      }
    }
  }
}

.message-footer {
  border-top: 1px solid $fog;
  border-bottom: 1px solid $fog;
  margin-top: 20px;
  padding: 12px 16px 16px 16px;
  display: flex;
  flex-direction: column;
  p {
    color: $strom;
  }
  .comments-counter {
    display: flex;
    align-items: center;
    margin-top: 8px;
    svg {
      width: 14px;
      height: 9px;
      margin-right: 8px;
    }
  }

  .author-info {
    flex: 5;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    margin-top: 8px;
    p,
    b {
      font-size: 0.75rem;
    }
    .desktop-spacer {
      display: none;
    }
    .updater {
      margin-top: 8px;
    }
  }
  .actions {
    flex: 5;
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;

    button {
      margin-left: 12px;
    }
  }
}

.placeholder {
  color: $strom;
}
.load-more-comments {
  padding-left: 15px;
  margin-top: 18px;
  margin-bottom: 18px;
  cursor: pointer;
}
.sub-replies {
  width: 100%;
  margin-left: 42px;
}
.sub-reply {
  border-top: 1px solid $fog;
  padding-top: 18px;
  display: flex;
  flex-wrap: wrap;
  margin-top: 18px;

  .sub-reply-body {
    width: calc(100% - 42px);

    .author {
      font-size: 0.85rem;
    }

    .removed-message {
      margin-top: 6px;
      color: $strom;
    }

    .sub-reply-message {
      margin-top: 6px;
    }
  }
  .sub-reply-footer {
    padding-left: 42px;
    margin-top: 8px;
    display: flex;
    p {
      font-size: 0.75rem;
    }
  }
}
.delete {
  p {
    color: $midnight;
  }

  cursor: pointer;
  &.deleted {
    color: $dusk;
  }
}
.user-profile-avatar,
.user-profile-image {
  margin-right: 10px;
}
.comment {
  display: flex;
  margin: 18px 15px 18px 15px;
  padding: 18px 0px 0 0px;
  flex-wrap: wrap;
  border-top: 1px solid $fog;
  .deleted-message {
    color: $sunset;
  }
  &:first-of-type {
    margin-top: 12px;
  }

  .comment-body {
    width: calc(100% - 42px);

    .author {
      font-size: 0.875rem;
    }
    .comment-message {
      margin-top: 6px;
    }

    .removed-message {
      margin-top: 6px;
      color: $strom;
    }
  }

  .time-wrapper {
    display: flex;
    p {
      font-size: 0.75rem;
    }

    .posted-time {
      color: $strom;
    }
  }
  .tooltip-content {
    bottom: 24px;
  }
  .reply {
    cursor: pointer;
    color: $twilight;
  }
  .comment-footer {
    width: 100%;
    margin-top: 8px;
    padding-left: 42px;
    display: flex;
    flex-wrap: wrap;
  }
}
.show-more {
  margin-top: 18px;
  width: 100%;
  cursor: pointer;
  p {
    font-size: 0.75rem;
    color: $midnight;
  }
  &.base-padding {
    padding-left: 42px;
  }
}
.message-comments {
  order: 6;
  width: 100%;

  .comment:first-of-type {
    border-top: 0px;
    margin-top: 0;
    padding-top: 0;
  }
}

.comment-section {
  width: 100%;
  order: 7;
  padding: 14px 15px;
  background: $snow;
  border-top: 1px solid $sleet;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

@media (min-width: 480px) and (orientation: landscape) {
}

@media (min-width: 544px) {
}

@media (min-width: 768px) {
}

@media (min-width: 1024px) {
  .locked-icon {
    > svg {
      width: 52px;
      height: 40px;
    }
  }

  .no-subscription {
    max-width: 600px;
    margin: 48px auto;
  }

  .no-subscription-copy {
    font-size: 1.5rem;
    line-height: 2.25rem;
    margin-top: 42px;
  }
  .sub-copy {
    font-size: 1.125rem;
  }
  .get-started-button {
    margin: 42px auto 0 auto;
  }

  .desktop-only {
    display: block;
  }
  .load-more-comments {
    order: 7;
    padding: 0;
    margin: 0px 0 18px 30px;
  }
  .comment-section {
    width: 100%;
    order: 8;
    padding: 24px 30px 27px 30px;
    background: $snow;
    border-top: 1px solid $sleet;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
  }
  .sub-reply {
    .sub-reply-body {
      .sub-reply-message {
        margin-top: 8px;
      }
      .removed-message {
        color: $strom;
        margin-top: 8px;
      }
    }
    .sub-reply-footer {
      margin-top: 12px;
    }
  }
  .comment {
    margin: 24px 30px 24px 30px;
    padding-top: 24px;
    max-width: 60%;
    .comment-body {
      .removed-message {
        margin-top: 8px;
        color: $strom;
      }
    }

    .comment-body {
      .comment-message {
        margin-top: 8px;
      }
    }
    .comment-footer {
      margin-top: 12px;
    }
  }
  .push-info-banner {
    p {
      text-align: center;
      padding: 6px 0;
    }
  }
  .new-message-wrapper {
    width: 100%;
    margin-top: 32px;
  }
  .new-message {
    padding: 30px 30px 32px 30px;
    grid-auto-columns: 1fr;
    display: grid;
    row-gap: 0;
    margin: 0;
    .title {
      grid-column: 1 / span 7;
      grid-row: 1;
      .default-inputfield {
        width: 100%;
      }
    }
    .new-message-body {
      margin-top: 24px;
      grid-column: 1 / span 7;
      grid-row: 2 / span 4;
    }
    .new-message-audience {
      grid-column: 9 / span 2;
      grid-row: 1 / span 2;
    }
    .new-message-actions {
      grid-column: 9 / span 2;
      grid-row: 5 / span 1;
    }
  }

  .update-message {
    padding: 30px 30px 32px 30px;
    grid-auto-columns: 1fr;
    display: grid;
    row-gap: 0;

    .title {
      grid-column: 1 / span 7;
      grid-row: 1;
      .default-inputfield {
        width: 100%;
      }
    }
    .update-message-body {
      margin-top: 24px;
      grid-column: 1 / span 7;
      grid-row: 2 / span 4;
    }
    .update-message-audience {
      grid-column: 9 / span 2;
      grid-row: 1 / span 2;
    }
    .update-message-actions {
      grid-column: 9 / span 2;
      grid-row: 5 / span 1;
    }
  }

  .manager-event-messages {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-end;
  }

  .page-title {
    width: 100%;
  }
  .page-subtitle {
    margin-bottom: 22px;
  }
  .create-message-button {
    margin: 0 0 22px 0;
  }

  .message {
    margin-top: 20px;
    width: 100%;
  }

  .message-wrapper {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .message-subject {
    order: 0;
    flex: 75%;
    padding: 24px 0 0 30px;
  }
  .message-body {
    order: 3;
    width: 75%;
    padding: 20px 30px 0 30px;
  }
  .audience-label {
    display: flex;
    justify-content: flex-end;
  }
  .message-footer {
    order: 4;
    flex: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 10px 30px 12px 30px;
    background-color: transparent;
    border-top: 1px solid $fog;
    border-bottom: 1px solid $fog;
    border-radius: 0;
    margin-bottom: 18px;
    .comments-counter {
      margin: 0;
      svg {
        width: 19px;
        height: 12px;
      }
    }
    .author-info {
      flex: 60%;
      flex-direction: row;
      justify-content: flex-start;
      margin: 0;
      p {
        font-size: 0.875rem;
      }

      .desktop-spacer {
        display: block;
        margin: 0 8px;
      }

      .responsive-linebreak {
        display: none;
      }
      .updater {
        margin: 0;
      }
    }
  }
}
</style>
